import { Warning } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { ScrollLock } from '@/components/shared/ScrollLock';

/**
 * Double-booking confirmation. When a new or rescheduled diary event overlaps
 * something already in the diary, we WARN and let the user decide — we never
 * silently save, and we never hard-block (a barrister may legitimately need two
 * things in the same slot). Shows exactly what clashes so the choice is informed.
 */

export interface ClashItem {
  title: string;
  startAt: string; // ISO
  endAt: string;   // ISO
}

const hhmm = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

export function DoubleBookConfirm({
  clashes,
  onConfirm,
  onCancel,
}: {
  clashes: ClashItem[];
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <ScrollLock />
      <div className="my-auto w-full max-w-md rounded-xl border border-amber-200 dark:border-amber-500/30 bg-card shadow-pop">
        <div className="flex items-start gap-3 border-b border-border px-6 py-4">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-500/15">
            <Warning className="h-4 w-4 text-amber-700 dark:text-amber-300" weight="fill" />
          </span>
          <div>
            <div className="text-base font-semibold text-foreground">You're already booked at this time</div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              This slot overlaps {clashes.length === 1 ? 'an event' : `${clashes.length} events`} already in the diary.
            </div>
          </div>
        </div>

        <div className="px-6 py-4">
          <div className="space-y-2">
            {clashes.map((c, i) => (
              <div key={i} className="flex items-center justify-between rounded-md border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-3 py-2">
                <span className="truncate text-sm font-medium text-foreground">{c.title || 'Untitled event'}</span>
                <span className="ml-3 shrink-0 text-xs font-medium text-amber-800 dark:text-amber-300">{hhmm(c.startAt)} – {hhmm(c.endAt)}</span>
              </div>
            ))}
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            Do you want to schedule another event at the same time anyway?
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
          <Button variant="outline" size="md" onClick={onCancel}>Go back</Button>
          <Button variant="primary" size="md" onClick={onConfirm}>Schedule anyway</Button>
        </div>
      </div>
    </div>
  );
}
